import { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

import customer1 from '../../assets/customers/customer_1.png';
import customer2 from '../../assets/customers/customer_2.png';
import customer3 from '../../assets/customers/customer_3.jpg';
import customer4 from '../../assets/customers/customer_4.jpg';

interface ClientLogo {
  src: string;
  alt: string;
}

const CLIENT_LOGOS: ClientLogo[] = [
  { src: customer1, alt: 'Client 1' },
  { src: customer2, alt: 'Client 2' },
  { src: customer3, alt: 'Client 3' },
  { src: customer4, alt: 'Client 4' }
];

interface ClientsMarqueeProps {
  title?: string;
  subtitle?: string;
  speed?: number;
  logos?: ClientLogo[];
  className?: string;
}

const ClientsMarquee = ({
  title = 'Trusted by Leading Businesses',
  subtitle = 'Join hundreds of companies using AmplifyMsg to grow their business',
  speed = 120,
  logos = CLIENT_LOGOS,
  className = '',
}: ClientsMarqueeProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });
  const trackRef = useRef<HTMLDivElement>(null);

  // Duplicate the logos enough times to fill the viewport and enable seamless looping
  const duplicated = [...logos, ...logos, ...logos, ...logos];

  // Pause on hover
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const pause = () => track.style.animationPlayState = 'paused';
    const resume = () => track.style.animationPlayState = 'running';

    track.addEventListener('mouseenter', pause);
    track.addEventListener('mouseleave', resume);
    return () => {
      track.removeEventListener('mouseenter', pause);
      track.removeEventListener('mouseleave', resume);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`py-16 lg:py-20 overflow-hidden ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {title && (
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-gray-400 text-sm lg:text-base max-w-xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>

      {/* Marquee track */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="relative"
      >
        {/* Gradient fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-24 lg:w-40 bg-linear-to-r from-(--color-navy) to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 lg:w-40 bg-linear-to-l from-(--color-navy) to-transparent z-10 pointer-events-none" />

        <div
          ref={trackRef}
          className="flex items-center gap-3 lg:gap-6 w-max animate-marquee"
          style={{
            animationDuration: `${speed}s`,
          }}
        >
          {duplicated.map((logo, i) => (
            <div
              key={i}
              className="shrink-0 flex items-center justify-center h-24 lg:h-32 px-6"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="max-h-full max-w-[180px] lg:max-w-[220px] object-contain rounded-2xl"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export { ClientsMarquee, type ClientLogo };
